import { useState } from 'react';
import { X, Trash2, AlertCircle } from 'lucide-react';
import { posOrders } from '../../api/posAPI';
import usePosStore from '../../stores/posStore';
import ManagerApprovalModal from './ManagerApprovalModal';

const VOID_REASONS = [
  'Wrong order',
  'Customer changed mind',
  'Out of stock',
  'Others',
];

function fmtPrice(centavos) {
  return `₱${((centavos || 0) / 100).toFixed(2)}`;
}

export default function CancelItemsModal({ order, onClose, onVoided }) {
  const { posToken } = usePosStore();
  const [selected, setSelected] = useState(new Set());
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [showApproval, setShowApproval] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const activeItems = order?.items?.filter(i => !i.voidedAt) || [];

  const toggle = (itemId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const effectiveReason = reason === 'Others' ? otherReason.trim() : reason;
  const canRequest = selected.size > 0 && reason && (reason !== 'Others' || otherReason.trim());

  const handleRequestApproval = () => {
    setError(null);
    if (!canRequest) return;
    setShowApproval(true);
  };

  const handleApproved = async (approvedBy) => {
    setShowApproval(false);
    setLoading(true);
    setError(null);
    try {
      const res = await posOrders.voidItems(posToken, order._id, {
        itemIds: [...selected],
        reason: effectiveReason,
        approvedBy,
      });
      onVoided(res.data.order);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Void failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col max-h-[85vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              <h2 className="font-bold text-gray-900">Cancel Items</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Items */}
          <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
            {error && (
              <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <p className="text-xs text-gray-500 mb-3">Select items to cancel. Requires manager or cashier approval.</p>

            {activeItems.map((item) => {
              const isChecked = selected.has(item._id.toString());
              return (
                <label
                  key={item._id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isChecked ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggle(item._id.toString())}
                    className="w-4 h-4 accent-red-500 flex-shrink-0"
                  />
                  <span className="text-sm font-semibold text-gray-500 w-6 text-right flex-shrink-0">
                    {item.quantity}×
                  </span>
                  <span className="flex-1 text-sm font-medium text-gray-900">{item.name}</span>
                  <span className="text-sm font-semibold text-gray-700 flex-shrink-0">
                    {fmtPrice(item.subtotal)}
                  </span>
                </label>
              );
            })}
          </div>

          {/* Reason */}
          <div className="px-5 py-3 border-t border-gray-100 flex-shrink-0 space-y-2">
            <label className="block text-xs font-semibold text-gray-700">Reason (required)</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              <option value="">— select reason —</option>
              {VOID_REASONS.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            {reason === 'Others' && (
              <input
                type="text"
                placeholder="Specify reason…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                autoFocus
              />
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 px-5 py-4 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRequestApproval}
              disabled={!canRequest || loading}
              className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              <Trash2 className="h-4 w-4" />
              {loading ? 'Voiding…' : 'Request Approval'}
            </button>
          </div>
        </div>
      </div>

      <ManagerApprovalModal
        isOpen={showApproval}
        action="void_items"
        onApprove={handleApproved}
        onClose={() => setShowApproval(false)}
      />
    </>
  );
}
